import { useEffect, useState } from 'react';
import { ChecklistsAPI } from '../../lib/apiChecklists';
import { useAuth } from '../../contexts/AuthContext';
import type { ChecklistStepRun } from '../../types/checklists';

type Draft = { notes: string; skip_reason: string };
export function useChecklistDraft(step: ChecklistStepRun | undefined, editable: boolean) {
  const { user } = useAuth();
  const key = step ? `checklist-draft:${user?.id}:${step.id}` : '';
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [state, setState] = useState<Record<string, string>>({});
  let cached: Draft | undefined;
  try { cached = key ? JSON.parse(sessionStorage.getItem(key) || 'null') : undefined; } catch { /* No browser recovery available. */ }
  const draft = drafts[key] || cached || { notes: step?.notes || '', skip_reason: step?.skip_reason || '' };
  const change = (patch: Partial<Draft>) => {
    const value = { ...draft, ...patch };
    setDrafts(previous => ({ ...previous, [key]: value }));
    setState(previous => ({ ...previous, [key]: 'Unsaved changes' }));
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* Server autosave remains available. */ }
  };
  const dirty = drafts[key] || cached;
  const notes = dirty?.notes;
  const reason = dirty?.skip_reason;
  useEffect(() => {
    if (!step || !editable || notes === undefined || reason === undefined) return;
    let live = true;
    const timer = setTimeout(() => {
      setState(previous => ({ ...previous, [key]: 'Saving…' }));
      ChecklistsAPI.updateStepRun(step.id, { notes, skip_reason: reason }).then(() => {
        // Do not clear recovery for edits typed while this request was in flight.
        try {
          const recovery = JSON.parse(sessionStorage.getItem(key) || 'null');
          if (recovery?.notes === notes && recovery?.skip_reason === reason) sessionStorage.removeItem(key);
        } catch { /* Ignore unavailable session storage. */ }
        if (live) setState(previous => ({ ...previous, [key]: 'Saved' }));
      }).catch(() => {
        if (live) setState(previous => ({ ...previous, [key]: 'Could not save. Your draft is kept in this browser; edit to retry.' }));
      });
    }, 700);
    return () => { live = false; clearTimeout(timer); };
  }, [key, step?.id, editable, notes, reason]);
  return { notes: draft.notes, skipReason: draft.skip_reason, setNotes: (notes: string) => change({ notes }), setSkipReason: (skip_reason: string) => change({ skip_reason }), saveState: state[key] || (cached ? 'Recovered draft' : 'Saved') };
}
